// Registers a Quill `header` format that can never be level 1.
//
// The toolbar only offers H2/H3, but Quill's clipboard maps a pasted <h1>
// (from Word, Google Docs, or another website) onto header:1 and renders a
// real <h1> inside the editor. The published page already has exactly one H1
// — the title — so a body H1 would be a second one.
//
// Clamping at the blot level covers every route into the editor: paste,
// drag-and-drop, and the initial value load of an existing draft. It also
// means the submitter *sees* the demotion in the editor rather than having it
// happen invisibly at submit time. See lib/contentHtml.ts for the submit-time
// and server-side enforcement that backs this up.

import ReactQuill from 'react-quill';

let registered = false;

/** header:1 -> header:2. Every other value (2, 3, false) passes through. */
function clampLevel(value: unknown): unknown {
  if (value === 1 || value === '1' || String(value).toUpperCase() === 'H1') return 2;
  return value;
}

export function registerNoH1Header(): void {
  if (registered) return;
  registered = true;

  const Quill = ReactQuill.Quill;
  // Quill's dynamic import isn't precisely typed; this is the Header block
  // format, whose `create`/`formats` statics come from Parchment.
  const Header = Quill.import('formats/header') as {
    new (...args: unknown[]): unknown;
    create(value: unknown): HTMLElement;
    formats(node: HTMLElement): unknown;
  };

  class NoH1Header extends (Header as { new (...args: unknown[]): object }) {
    // Applying the format (toolbar, or a delta from a paste) never builds an H1.
    static create(value: unknown): HTMLElement {
      return (Header.create as (v: unknown) => HTMLElement).call(this, clampLevel(value));
    }

    // Reading the format off a pasted <h1> node reports it as a level 2, so
    // the delta Quill inserts carries header:2.
    static formats(node: HTMLElement): unknown {
      return clampLevel((Header.formats as (n: HTMLElement) => unknown).call(this, node));
    }
  }

  // blotName ('header') and tagName are inherited from Header, so this
  // replaces the built-in registration.
  Quill.register(NoH1Header as never, true);
}
